import {
  GeckoComponentModelType,
  GeckoMeasurement,
  GeckoMeasurementUnit,
} from 'types'

import type { RawRow } from './b313TableA1Data'
import { RAW } from './b313TableA1Data'

/**
 * ASME B31.3-2022 Table A-1 — allowable-stress material catalog for the FFS picker
 * (`MetalAllowableStressTable` / `MaterialStressPickerAccessory`). Replaces the
 * previous ad-hoc carbon-steel table whose values were SME-flagged as incorrect.
 *
 * This module owns the runtime CONTRACT + LOOKUP: the `AllowableStressRec` type, the
 * `build` mapping from the compact source rows to `GeckoMeasurement`, and the
 * component-type registry. The raw numeric catalog itself lives in the sibling
 * `b313TableA1Data.ts` (pure data, machine-validated in allowableStressValues.test.ts).
 * SME sign-off against the source PDF is still recommended before production; the
 * carbon-steel & stainless sections of Table A-1 are TODO.
 */

/** Metal temperatures (°F) tabulated by B31.3 Table A-1. `≤100` is stored as key 100. */
export const STRESS_TEMPS_F = [
  100, 200, 300, 400, 500, 600, 650, 700, 750, 800, 850, 900, 950, 1000, 1050,
  1100, 1150, 1200,
] as const

export type StressTempF = (typeof STRESS_TEMPS_F)[number]

export type ProductForm =
  | 'Pipes'
  | 'Plates'
  | 'Forgings & Fittings'
  | 'Castings'

export interface AllowableStressRec {
  /** Stable id: source + section + line. Unique across sections even if line numbers repeat. */
  sourceId: string
  /** B31.3 Table A-1 line number within the captured section (for display/audit). */
  sourceLine: number
  productForm: ProductForm
  /** Nominal composition (+ any parenthetical, e.g. "1Cr–½Mo (A387 Gr.12 Cl.1)"). */
  material: string
  spec: string
  grade: string | null
  /** Class/Condition, e.g. "1" | "2" | "3" — distinguishes same-grade rows. */
  materialClass: string | null
  uns: string | null
  pNo: string
  minTemp: GeckoMeasurement
  maxTemp: GeckoMeasurement
  minTensile: GeckoMeasurement
  /** SMYS in ksi; NULL where the source lists no min yield (B31.3 lines 7–12). */
  minYield: GeckoMeasurement | null
  /** Allowable stress S(T). Sparse — a temperature is absent when > maxTemp. */
  allowableStress: Readonly<Partial<Record<StressTempF, GeckoMeasurement>>>
}

const ksi = (v: number): GeckoMeasurement =>
  GeckoMeasurement.CreateFromValue(
    v * 1000,
    GeckoMeasurementUnit.PoundsForcePerSqInch
  )

const degF = (v: number): GeckoMeasurement =>
  GeckoMeasurement.CreateFromValue(v, GeckoMeasurementUnit.Fahrenheit)

function build(r: RawRow): AllowableStressRec {
  const allowableStress: Partial<Record<StressTempF, GeckoMeasurement>> = {}
  STRESS_TEMPS_F.forEach((t, i) => {
    const v = r.s[i]
    if (v !== null && v !== undefined) allowableStress[t] = ksi(v)
  })
  return {
    sourceId: `B31.3-2022:A-1:low-alloy:${r.line}`,
    sourceLine: r.line,
    productForm: r.form,
    material: r.material,
    spec: r.spec,
    grade: r.grade,
    materialClass: r.cls,
    uns: r.uns,
    pNo: r.pNo,
    minTemp: degF(r.minTempF),
    maxTemp: degF(r.maxTempF),
    minTensile: ksi(r.tensile),
    minYield: r.yield === null ? null : ksi(r.yield),
    allowableStress,
  }
}

/** B31.3 piping catalog (product forms: Pipes / Plates / Forgings & Fittings / Castings). */
export const pipingAllowableStressRecords: readonly AllowableStressRec[] =
  RAW.map(build)

const PIPING_COMPONENT_TYPES: readonly GeckoComponentModelType[] = [
  GeckoComponentModelType.PipeSection,
  GeckoComponentModelType.PipeElbow,
  GeckoComponentModelType.PipeTJoint,
  GeckoComponentModelType.PipeEccentricReducer,
]

/**
 * Material dataset by component-model type. Piping component types map to the B31.3
 * catalog; other types (pressure-vessel/tank) are intentionally absent for now, so
 * `getAllowableStressRecords` returns null and the picker button is hidden.
 */
export const ALLOWABLE_STRESS_BY_COMPONENT_TYPE: Partial<
  Record<GeckoComponentModelType, readonly AllowableStressRec[]>
> = Object.fromEntries(
  PIPING_COMPONENT_TYPES.map((t) => [t, pipingAllowableStressRecords])
)

/** Records for a component type, or null when no dataset exists (⇒ hide the picker). */
export const getAllowableStressRecords = (
  t: GeckoComponentModelType | undefined
): readonly AllowableStressRec[] | null =>
  t === undefined ? null : (ALLOWABLE_STRESS_BY_COMPONENT_TYPE[t] ?? null)
